import { Component, computed, inject, input, output } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DROPDOWNLIST } from '@progress/kendo-angular-dropdowns';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { TransitionMiniDto, TransitionRequestDto } from '@unity/clients';
import { TextareaControlComponent } from '@unity/components/forms/textarea-control/textarea-control.component';
import { SelectorDateControlComponent } from '@unity/forms/selector-date-control/selector-date-control.component';
import { DetailFormService } from '@unity/services/detail-form.service';
import { IconService } from '@unity/services/icon.service';
import { DialogSharedComponent } from '@unity/shared/presentation/dialog-shared/dialog-shared.component';
import { nonZeroFormControl, optionalDateFormControl, optionalStringFormControl, stringFormControl } from '@unity/utils/form-controls';
import { FormGroupControlSchema } from '@unity/utils/types';

class TransitionRequestSchema implements FormGroupControlSchema<TransitionRequestDto> {
  public id: FormControl<number>;
  public comment: FormControl<string | null>;
  public date: FormControl<Date | null>;

  public constructor(transition?: TransitionMiniDto) {
    this.id = nonZeroFormControl({ disabled: true, value: transition?.id });
    this.comment = transition?.is_comment_required ? stringFormControl() : optionalStringFormControl();
    this.date = optionalDateFormControl({ value: transition?.is_date_overrideable ? new Date() : undefined });
  }
}

@Component({
  selector: 'unity-dialog-transition-confirmation-shared',
  imports: [
    KENDO_GRID,
    KENDO_DROPDOWNLIST,
    KENDO_BUTTON,
    ReactiveFormsModule,
    DialogSharedComponent,
    TextareaControlComponent,
    SelectorDateControlComponent,
  ],
  templateUrl: './dialog-transition-confirmation-shared.component.html',
  styleUrl: './dialog-transition-confirmation-shared.component.scss',
})
export class DialogTransitionConfirmationSharedComponent {
  private readonly iconService = inject(IconService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly detailFormService = inject(DetailFormService);

  protected worflowIcon = this.iconService.arrowRightIcon;

  protected readonly $formGroup = computed(() =>
    this.formBuilder.group<TransitionRequestSchema>(new TransitionRequestSchema(this.$transition()))
  );

  public readonly $transition = input.required<TransitionMiniDto>({ alias: 'transition' });

  public closeEvent = output<void>();
  public transitionEvent = output<TransitionRequestDto>();

  protected onClose() {
    this.closeEvent.emit();
  }

  protected onSave() {
    const formGroup = this.$formGroup();
    formGroup.updateValueAndValidity();
    if (formGroup.valid) {
      this.transitionEvent.emit(formGroup.getRawValue());
      this.closeEvent.emit();
    } else {
      this.detailFormService.handleFormErrors(formGroup);
    }
  }
}
